import type { UnsubscribeListener } from '@reduxjs/toolkit';
import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import { addAppListener } from '@src/providers/AppStateProvider/types';
import type { OptionsPanelConfig } from '@src/types/LayoutConfig';
import { EMPTY_OBJECT, useDynamicCallback } from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { useEffect } from 'react';
import { selectMeta, selectTabData } from '../Filter/selectors';
import { hydratePersistedTabState } from './../Filter/filterSlice';

export interface OptionsProps extends Omit<OptionsPanelConfig, 'type' | 'closable' | 'editable'> {
  id: string;
  updateConfig: (panelData: Partial<OptionsPanelConfig>) => void;
}

/**
 * Hooks that hydrates the option redux slice with the persisted state,
 * conversely, it updates the app config with the content of the redux slice.
 */
export function useOptionsTab({ updateConfig, id, ...data }: OptionsProps) {
  const dispatch = useAppStateDispatch();
  const populatePersistedTabState = useDynamicCallback(() =>
    dispatch(hydratePersistedTabState({ ...(data || EMPTY_OBJECT), panelID: id }))
  );
  const meta = useAppStateSelector(selectMeta);

  useEffect(() => {
    // Add listener which updates data whenever the state is updated
    const unsubscribe = dispatch(
      addAppListener({
        predicate: (_, currentState, previousState) => {
          return !isEqual(selectTabData(currentState, id), selectTabData(previousState, id));
        },
        effect: async (_, listenerApi) => {
          const newState = listenerApi.getState();
          const tabData = selectTabData(newState, id);
          updateConfig(tabData);
        },
      })
    );
    // Cast below should be fixed once we use `withTypes` more
    // See: https://github.com/reduxjs/redux-toolkit/discussions/2798#discussioncomment-3917583
    return unsubscribe as unknown as UnsubscribeListener;
  }, [updateConfig, id, dispatch]);

  // When the id updates, hydrate redux with persisted data.
  useEffect(() => {
    if (meta.optionSecurityBySymbol.size > 0) {
      populatePersistedTabState();
    }
  }, [dispatch, populatePersistedTabState, id, meta.optionSecurityBySymbol.size]);
}
